import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaDollarSign, FaBriefcase, FaCalendarAlt, FaSpinner, FaChartLine, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { earningsService } from '../../api/earningsService';

const DriverEarningsModal = ({ isOpen, onClose, driverId, driverName }) => {
  const [earnings, setEarnings] = useState([]);
  const [driverData, setDriverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    if (isOpen && driverId) {
      fetchDriverEarnings();
    }
  }, [isOpen, driverId, period]);

  const fetchDriverEarnings = async () => {
    try {
      setLoading(true);
      const response = await earningsService.getDriverEarnings(driverId, { period });
      
      if (response.success) {
        setEarnings(response.data.earnings || []);
        setDriverData(response.data.driver);
      } else {
        toast.error(response.message || 'Failed to load driver earnings');
      }
    } catch (error) {
      console.error('Error fetching driver earnings:', error);
      toast.error('Failed to load driver earnings data');
    } finally {
      setLoading(false);
    }
  };

  const exportDriverEarnings = () => {
    if (earnings.length === 0) {
      toast.warning('No earnings data to export');
      return;
    }

    // Create CSV data
    const csvContent = [
      ['Date', 'Job ID', 'Earnings', 'Job Type', 'Status'],
      ...earnings.map(earning => [
        new Date(earning.createdAt?.toDate?.() || earning.createdAt).toLocaleDateString(),
        earning.jobId || 'N/A',
        `$${earning.totalPay || 0}`,
        earning.jobType || 'Standard',
        earning.status || 'Completed'
      ])
    ].map(row => row.join(',')).join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver-earnings-${driverName || driverId}-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Driver earnings exported successfully');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getPeriodLabel = (period) => {
    const labels = {
      week: 'This Week',
      month: 'This Month',
      quarter: 'This Quarter',
      year: 'This Year'
    };
    return labels[period] || period;
  };

  if (!isOpen || !driverId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaUser className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Driver Earnings Details</h2>
              <p className="text-green-100">{driverName || `Driver ${driverId}`}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-200 transition-colors"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Driver Info */}
          {driverData && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Driver Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-900">{driverData.firstName} {driverData.lastName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900">{driverData.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{driverData.email || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Period Selection */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Earnings Period</h3>
              <button
                onClick={exportDriverEarnings}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
            </div>
            
            <div className="flex space-x-4">
              {['week', 'month', 'quarter', 'year'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-4 py-2 rounded-md font-medium ${
                    period === p
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getPeriodLabel(p)}
                </button>
              ))}
            </div>
          </div>

          {/* Earnings Summary */}
          {earnings.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FaDollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">Total Earnings</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(earnings.reduce((sum, e) => sum + (e.totalPay || 0), 0))}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaBriefcase className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">Jobs Completed</p>
                    <p className="text-lg font-bold text-gray-900">{earnings.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FaChartLine className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">Average Per Job</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(earnings.reduce((sum, e) => sum + (e.totalPay || 0), 0) / earnings.length)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FaCalendarAlt className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">Period</p>
                    <p className="text-lg font-bold text-gray-900 capitalize">{getPeriodLabel(period)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Earnings Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Earnings History</h3>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FaSpinner className="animate-spin h-8 w-8 text-green-500" />
                <span className="ml-2 text-gray-600">Loading earnings...</span>
              </div>
            ) : earnings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Earnings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {earnings.map((earning) => (
                      <tr key={earning.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(earning.createdAt?.toDate?.() || earning.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {earning.jobId || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(earning.totalPay)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {earning.jobType || 'Standard'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            earning.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            earning.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {earning.status || 'Completed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FaChartLine className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No earnings data found for this period</p>
                <p className="text-sm text-gray-400">Try selecting a different time period</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverEarningsModal;
